import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setIsMobileCatOpen(false);
  }, [location.pathname]);

  const isTransparent = !isScrolled && location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            className={`font-heading text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap transition-colors duration-300 ${
              isTransparent ? 'text-background-50' : 'text-foreground-950'
            }`}
          >
            Kakobuy<span className={isTransparent ? 'text-primary-300' : 'text-primary-500'}>.study</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg whitespace-nowrap ${
                isTransparent
                  ? 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              Strona Główna
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors rounded-lg whitespace-nowrap cursor-pointer ${
                  isTransparent
                    ? 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                Kategorie
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 max-h-96 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-accent-50 transition-colors whitespace-nowrap"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                        <i className={`${cat.icon} text-base`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/kakobuy-spreadsheet"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg whitespace-nowrap ${
                isTransparent
                  ? 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              Poradnik
            </Link>

            <Link
              to="/kakobuy-guide"
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg whitespace-nowrap ${
                isTransparent
                  ? 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              Przewodnik Zakupowy
            </Link>
          </div>

          {/* CTA & Mobile Toggle */}
          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Kakobuy Spreadsheet
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-xs"></i>
              </span>
            </a>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                isTransparent
                  ? 'text-background-50 hover:bg-background-50/10'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
              aria-label="Menu"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`text-xl ${isOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-home-4-line text-base"></i>
              </span>
              Strona Główna
            </Link>

            <Link
              to="/kakobuy-spreadsheet"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                <i className="ri-book-open-line text-base"></i>
              </span>
              Poradnik Kakobuy Spreadsheet
            </Link>

            <Link
              to="/kakobuy-guide"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                <i className="ri-guide-line text-base"></i>
              </span>
              Przewodnik Zakupowy
            </Link>

            {/* Mobile Categories Accordion */}
            <div className="border-t border-background-200/50 my-2 pt-2">
              <button
                onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
                className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                    <i className="ri-apps-2-line text-base"></i>
                  </span>
                  Kategorie Produktów
                </span>
                <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${isMobileCatOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </button>

              {isMobileCatOpen && (
                <div className="grid grid-cols-2 gap-1 px-2 mt-1 pb-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2.5 text-xs text-foreground-600 hover:text-foreground-950 hover:bg-accent-50 rounded-lg transition-colors"
                    >
                      <span className="w-4 h-4 flex items-center justify-center text-accent-500 flex-shrink-0">
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      <span className="truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-background-200/50 my-2 pt-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-external-link-line text-base"></i>
                </span>
                Kakobuy Spreadsheet
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}